import type {
  Place,
  PlaceFilters,
  PlaceType,
  TemporaryPlaceInput
} from "./types";

export function filterPlaces(places: Place[], filters: PlaceFilters): Place[] {
  return places.filter((place) => {
    const cityMatches = filters.city === "all" || place.city === filters.city;
    const typeMatches = filters.types.size === 0 || filters.types.has(place.type);
    const priorityMatches =
      filters.priority === "all" || place.priority === filters.priority;
    const confidenceMatches =
      filters.confidence === "all" || place.confidence === filters.confidence;

    return cityMatches && typeMatches && priorityMatches && confidenceMatches;
  });
}

export function getFilterOptions(places: Place[]): {
  cities: string[];
  types: PlaceType[];
} {
  return {
    cities: [...new Set(places.map((place) => place.city))].sort(),
    types: [...new Set(places.map((place) => place.type))].sort()
  };
}

export function mergePlaces(seed: Place[], local: Place[]): Place[] {
  const byId = new Map<string, Place>();

  seed.forEach((place) => byId.set(place.id, place));
  local.forEach((place) => byId.set(place.id, place));

  return [...byId.values()];
}

export function parseCoordinateInput(
  input: string
): { lat: number; lng: number } | null {
  const normalized = input.trim();
  const directMatch = normalized.match(
    /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/
  );
  const googleAtMatch = normalized.match(
    /@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/
  );
  const googleDataMatch = normalized.match(
    /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/
  );
  const match = directMatch ?? googleAtMatch ?? googleDataMatch;

  if (!match?.[1] || !match[2]) {
    return null;
  }

  const lat = Number(match[1]);
  const lng = Number(match[2]);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }

  return { lat, lng };
}

export function createTemporaryPlace(input: TemporaryPlaceInput): Place {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? `local-${crypto.randomUUID()}`
      : `local-${Date.now()}`;

  return {
    id,
    name: input.name.trim(),
    city: input.city.trim(),
    type: input.type,
    priority: "maybe",
    lat: input.lat,
    lng: input.lng,
    addressHint: input.addressHint.trim(),
    notes: input.notes.trim(),
    confidence: "unverified",
    source: "local"
  };
}
